/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    DocAddonBridge, AddonScenes, ToolbarButtonType,
    DocData, AddonInfo, Action,
    ToolbarButton, WebAddonElement, ContentContainer,
} from '@tencent/td-addon-sdk-docs';
import { ADDON_TYPE } from '@src/js/constant';
import { ADDON_ID_FORMULA, ADDON_ID_INSERT, DISABLE_ADDON_ID_FOR_DRIVE } from './addon-constants';
import { InjectorService } from '@tencent/workbench';
import { BaseAddonService } from '@src/framework/workbench/common/service/base-addon-service';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { ImageQuickItems } from '@src/framework/editor/src/js/constants/float-toobbar-data';
import IntelligentToolDescriptionCard from '@com-pc/intelligent-tool-description-card';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import i18n from '@i18next/i18n';
import { tdw } from '@tencent/tencent-doc-report';
import { TDWActionType, TDWModuleType, TDWOperationType, EventCodeType, TDWFileType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import throttle from 'lodash/throttle';
import BaseAddonHandler from './addon-handler-base';
import { isLogin } from '@src/js/common/utils';
import { ADDON_PREFIX } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import { AddonManagerEventsKeys, IAddonManager } from './addon-manager.interface';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import appConfigInstance from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { IQuickSettingManager } from '@src/framework/frame/components/quick-setting/quick-setting-manager.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { shouldDisableNetwork } from '@src/slide/infrastructure/module/dependent/socket';

export default class AddonHandler extends BaseAddonHandler implements IAddonHandler {
    // 快速工具栏中显示的按钮缓存
    public addonQuickItemsMap: Map<string, any> = new Map();

    public addonSceneItemsMap: Map<string, any> = new Map();

    public addonScenes: any;

    /**
     * 获取右键/快捷菜单中的插件信息
     * @param addonSceneInfoList 场景化类型
     * @param fromQuick 是否格式化成快捷工具栏的格式
     */
    public getSceneAddonListForMenu = throttle(async (type: string, extraAddonId, fromQuick?) => {
        const addonSceneInfoList = await DocAddonBridge.getSceneAddonList({
            scene: type,
            extraAddonIds: [extraAddonId],
        });
        this.addonSceneItemsMap.set(type, addonSceneInfoList);
        return this.formatAddonSceneList(addonSceneInfoList, extraAddonId, fromQuick);
    }, 500);

    protected quickSettingManager: IQuickSettingManager;

    // 外部实现方法，实例化时由外部传入
    protected updateToolbar: (toolbarData: string, entryPos: string) => void;
    private addonManager: IAddonManager;

    // 实例化
    public constructor(
        protected api: any,
        addonManager: IAddonManager,
        selectionService: ISelectionService,
        private linkManager?: ILinkManager,
    ) {
        super(api, addonManager, selectionService);
        this.updateToolbar = this.api.updateToolbar;
        this.addonScenes = AddonScenes;
        this.addonManager = addonManager;
        import(/* webpackChunkName: "service" */ '@com/quick-setting/quick-setting-manager')
            .then((module) => {
                this.quickSettingManager = module.default.getInstance();
            })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    }

    /**
     * 旧插件sdk的奇葩用法
     * 新sdk已经改变调用方式，不要继续在此方法中添加
     * 待后续完全切换到新插件sdk后，可删除此方法，同时修改其他文件中的调用方式，用其他public方法替代
     */
    public emitMessageToAddon = async (
        messageName: string,
        params: any,
        aid: string,
    ): Promise<string[] | undefined | WebAddonElement> => {
        if (messageName === '$onLoadToolbarButtons') {
            return await this.fetchToolbarButtonsType(aid);
        }
        if (messageName === '$onToolbarClick') {
            this.executeToolbarButton(aid);
        }

        if (messageName === '$onPasteUrl') {
            return await this.parseLinkAsAddon(params);
        }
    };

    public async fetchToolbarButtonsType(addonId: string): Promise<ToolbarButtonType[]> {
        const contentContainer = await this.fetchContentContainer(addonId);
        const toolbarButtons: ToolbarButton[] = contentContainer?.toolbar || [];
        return toolbarButtons.map((toolbarButton: ToolbarButton) => toolbarButton.type);
    }

    public async executeToolbarButton(addonId: string): Promise<void> {
        const contentContainer: ContentContainer | undefined = await this.fetchContentContainer(addonId);
        const toolbarButton: ToolbarButton | undefined = contentContainer?.toolbar?.[0];
        const action: Action | undefined = toolbarButton?.action;

        this.docAddonBridge.executeToolbarButtonAction(addonId, action);
    }

    public async parseLinkAsAddon(link: string): Promise<WebAddonElement> {
        const iframeInfo = await this.docAddonBridge.parseAsAddon(link);
        if (!iframeInfo) {
            return iframeInfo;
        }
        iframeInfo.aid = iframeInfo.addonId;
        iframeInfo.embedUrl = iframeInfo.embedURL;
        return iframeInfo;
    }

    public openFormulaDialog(formulaText: string) {
        return this.docAddonBridge.invokeFormulaAddonWithLaTexOCR(formulaText);
    }

    public updateQuickMenu = async (type, extraAddonId): Promise<void> => {
        const addonItems = ImageQuickItems.find(item => item.id === ActionType.ADDON);
        if (!addonItems) return;
        const newAddonSceneLists = await this.getSceneAddonListForMenu(type, extraAddonId, true);
        if (addonItems.dropdown) {
            addonItems.dropdown.data = newAddonSceneLists;
        }
        if (!this.quickSettingManager) return;
        const ver9 = type === AddonScenes.IMAGE ? 'doc_detail_pic_toolbar' : 'doc_detail_iframe_toolbar';
        tdw({
            opername: TDWOperationType.DOC_ADDON,
            module: TDWModuleType.ADDON_LIST,
            action: TDWActionType.LIST_VIEW,
            eventcode: EventCodeType.DOC_ADDON_ADDON_LIST_LIST_VIEW,
            ver9,
            file_type: TDWFileType.SLIDE,
        });
        this.quickSettingManager?.updateContent();
    };

    public updateIframeFloatMenu = async (type, extraAddonId): Promise<void> => {
        if (!this.linkManager) return;
        if (this.linkManager.linkFloatBarService.isShown()) {
            const config = this.linkManager.linkFloatBarService.getCurrentConfig();
            const addonItems = config.importConfig.find(item => item.id === ActionType.ADDON);
            if (!addonItems) return;
            const newAddonSceneLists = await this.getSceneAddonListForMenu(type, extraAddonId, true);
            addonItems.config.items = newAddonSceneLists;
            this.linkManager.linkFloatBarService.show(config, true);
        }
    };

    /**
     * 将插件场景信息格式化成菜单需要的格式
     * @param addonSceneInfoList 场景化信息
     * @param fromQuick 是否格式化成快捷工具栏的格式
     */
    public formatAddonSceneList(addonSceneInfoList, extraAddonId?, fromQuick?) {
        if (!addonSceneInfoList) {
            return;
        }
        let { installed, uninstalled } = addonSceneInfoList;
        if (!(DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.addonUnofficialIframe) ?? true)) {
            // cac配置屏蔽Iframe插件
            installed = installed.filter(item => DISABLE_ADDON_ID_FOR_DRIVE.indexOf(item.addonId) === -1);
            uninstalled = uninstalled.filter(item => DISABLE_ADDON_ID_FOR_DRIVE.indexOf(item.addonId) === -1);
        }
        let installedAddon: any = [];
        let uninstalledAddon: any = [];
        if (installed.length) {
            installedAddon = [{ id: ActionType.INSTALLED_ADDON, title: i18n.t('已添加插件'), disable: true }]
                .concat(installed.map(item => ({
                    id: `${ADDON_PREFIX}${item.addonId}`,
                    title: item.sceneName,
                    addon: item,
                    extraAddonId,
                })));
        }
        if (uninstalled.length) {
            if (installed.length) {
                uninstalledAddon = [{ id: ActionType.DIVIDER }];
            }
            uninstalledAddon = uninstalledAddon.concat([
                { id: ActionType.RECOMMEND_ADDON, title: i18n.t('推荐插件'), disable: true }]
                .concat(uninstalled.map(item => ({
                    id: `${ADDON_PREFIX}${item.addonId}`,
                    title: item.sceneName,
                    addon: item,
                    extraAddonId,
                }))));
        }
        const list = installedAddon.concat(uninstalledAddon);
        if (fromQuick) {
            return list.map((item) => {
                const { addon } = item;
                const dropdownItem: any = {
                    id: item.id,
                    icon: item.addon?.icons.small,
                    content: item.title,
                    disable: item.disable,
                    extraAddonId,
                };
                if (addon) {
                    const addonMaterial = {
                        // hover 时展示的 tooltip 配置:
                        title: addon.name,
                        vendorName: addon.vendorName,
                        vendorSite: addon.vendorSite,
                        imageUrl: addon.icons.intro,
                        description: addon.shortDesc,
                    };
                    const tooltip = {
                        titleComponent: IntelligentToolDescriptionCard,
                        titleProps: addonMaterial,
                    };
                    dropdownItem.tooltip = tooltip;
                }
                return dropdownItem;
            });
        }
        return list;
    }

    // 打开模板商城窗口里此插件的详情页
    public openAddonDetailPanel = (marketId: string): void => {
        import(/* webpackChunkName: "addon" */ '@tencent/docs-tmall-panel/dist/addon')
            .then((module) => {
                const { openAddonPanel, closeAddonPanel } = module;
                openAddonPanel({
                    type: 'slide',
                    page: 'detail',
                    query: {
                        id: marketId,
                    },
                    onOpenAddon: ({ id }) => {
                        this.openAddonPanel(id, closeAddonPanel);
                    },
                    onClose: () => {
                        this.updateAddonInfoList();
                    },
                });
            })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    };

    public handleAddonSceneItemClick = (menuId, tdwOption) => {
        const addonId = menuId.slice(6);
        let targetAddon;
        let marketId;
        let addonName;
        this.addonSceneItemsMap.forEach((typeItem) => {
            const { installed, uninstalled } = typeItem;
            const tempTarget = installed.find(item => item.addonId === addonId);
            const unInstallTarget = uninstalled.find(item => item.addonId === addonId);
            if (tempTarget) {
                targetAddon = tempTarget;
                addonName = tempTarget.name;
            }
            if (unInstallTarget) {
                marketId = unInstallTarget.marketId;
                addonName = unInstallTarget.name;
            }
        });
        if (targetAddon) {
            this.openAddonPanel(addonId);
        } else {
            this.openAddonDetailPanel(marketId);
        }
        tdw({
            opername: TDWOperationType.DOC_ADDON,
            module: TDWModuleType.ADDON_LIST,
            action: TDWActionType.CLK_ADDON,
            eventcode: EventCodeType.DOC_ADDON_ADDON_LIST_CLK_ADDON,
            ver6: addonId,
            ver7: addonName,
            file_type: TDWFileType.SLIDE,
            ...tdwOption,
        });
    };

    public getSceneType() {
        let type;
        const model = this.selectionService.getSelectedModel();
        switch (model?.type) {
            case UIModelType.IMAGE:
                type = AddonScenes.IMAGE;
                break;
            case UIModelType.SHAPE:
                if ((model.props as any).addon?.docElement?.type === 'iframe') {
                    type = AddonScenes.IFRAME;
                }
                break;
            default:
                type = AddonScenes.CURSOR;
        }
        return type;
    }

    public updateAddonInfoList(): void {
        this.docAddonBridge.fetchAddonList()
            .then((addonInfoList: AddonInfo[]) => {
                // 先清空，再添加
                this.clearAddonInfoList();
                this.handleFetchAddonInfoList(addonInfoList);
                addonInfoList.forEach((addonMeta) => {
                    InjectorService.get(BaseAddonService).updateAddonMenuSchema(addonMeta);
                });
            })
            .catch((e) => {
                window.log.error('fetchAddonInfoList error', e);
            });
    }

    /**
     * 初始化插件信息列表
     * @returns
     */
    protected initAddonInfoList(): void {
        this.docAddonBridge.fetchAddonList()
            .then((addonInfoList: AddonInfo[]) => {
                if (this.isLoadAddonInfoList) return;
                this.handleFetchAddonInfoList(addonInfoList);
                this.isLoadAddonInfoList = true;
                this.event.emit(AddonManagerEventsKeys.ADDON_INFO_LOAD_READY);

                let filteredAddonInfoList = addonInfoList;
                if (!(DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.addonUnofficialIframe) ?? true)) {
                    // cac配置屏蔽iframe插件
                    filteredAddonInfoList = addonInfoList.filter(addonMeta => (DISABLE_ADDON_ID_FOR_DRIVE
                        .indexOf(addonMeta?.addonId) === -1));
                }
                const updateAddonMenuSchema = () => filteredAddonInfoList.forEach((addonMeta) => {
                    InjectorService.get(BaseAddonService).updateAddonMenuSchema(addonMeta);
                });
                if (this.addonManager.workbenchLoaded) {
                    updateAddonMenuSchema();
                } else {
                    eventBus.on(EventKeys.WORKBENCH_IS_MOUNT, () => {
                        updateAddonMenuSchema();
                    });
                }

                // workbench，屏蔽
            })
            .catch((e) => {
                window.log.error('fetchAddonInfoList error', e);
            });
    }

    /**
     * 初始化图片插件场景化信息列表
     * @returns
     */
    protected initAddonImageSceneList(): void {
        this.fetchAddonSceneInfoList(AddonScenes.IMAGE)
            .then((imageSceneList) => {
                this.addonSceneItemsMap.set(AddonScenes.IMAGE, imageSceneList);
            });
    }

    /**
     * 初始化iframe插件场景化信息列表
     * @returns
     */
    protected initAddonIframeSceneList(): void {
        this.fetchAddonSceneInfoList(AddonScenes.IFRAME)
            .then((imageSceneList) => {
                this.addonSceneItemsMap.set(AddonScenes.IFRAME, imageSceneList);
            });
    }

    /**
     * 初始化iframe插件场景化信息列表
     * @returns
     */
    protected initAddonCurosrSceneList(): void {
        this.fetchAddonSceneInfoList(AddonScenes.CURSOR)
            .then((imageSceneList) => {
                this.addonSceneItemsMap.set(AddonScenes.CURSOR, imageSceneList);
            });
    }

    /**
     * 依据插件数据渲染插入插件入口ui
     * @param addonId 插件id
     */
    protected renderAddon(addonId: string): void {
        const { isOfficial } = this.addonInfoMap.get(addonId) || {};
        const addonMeta: any = this.addonMetaMap.get(addonId) || {};

        // 渲染非官方插件入口
        if (!isOfficial) {
            this.updateToolbar(addonMeta, ADDON_TYPE.UNOFFICIAL);
            return;
        }

        // 按原来的逻辑，官方插件有可能在多个位置展示，以下条件通过后不 return

        // 渲染插入latex公式入口
        if (addonId === ADDON_ID_FORMULA) {
            this.updateToolbar(addonMeta, ADDON_TYPE.FORMULA);
        }

        // 渲染插入第三方内容入口
        if (addonId === ADDON_ID_INSERT) {
            this.updateToolbar(addonMeta, ADDON_TYPE.INSERT);
        }
    }

    // 获取插件源数据列表
    protected fetchAddonSceneInfoList(type) {
        return DocAddonBridge.getSceneAddonList({ scene: type });
    }

    protected handleFetchAddonInfoList(addonInfoList: AddonInfo[]): void {
        // window.log.info('[addon info list]', addonInfoList);
        addonInfoList.forEach((addonInfo: AddonInfo) => {
            const addonMeta: any = this.getCompatibleAddon(addonInfo);
            this.saveAddonMeta(addonMeta);
            this.saveAddonInfo(addonInfo);
            this.renderAddon(addonInfo.addonId);
        });
    }

    protected clearAddonInfoList(): void {
        this.addonMetaMap.clear();
        this.addonInfoMap.clear();
    }

    protected getAddonInfo(): void {
        if (shouldDisableNetwork()) {
            return;
        }
        const docData: DocData = this.getDocData();
        this.initAddonOptions(docData);
        if (appConfigInstance.getConfig('custom.addon') ?? true) {
            this.initAddonInfoList();
            if (isLogin()) {
                this.initAddonImageSceneList();
                this.initAddonIframeSceneList();
                this.initAddonCurosrSceneList();
            }
        }
    }
}
